import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import {
  UK, PIPELINE_STAGES, PIPELINE_FILTERS, fmt, fmtMoney, stageMeta, dealAccent, isActiveDeal,
} from './pipelineFormShared';

const PAGE_SIZE = 15;

export default function SalesPipeline() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [filterTab, setFilterTab] = useState<string>('All');
  const [viewMode, setViewMode] = useState<'list' | 'board'>('list');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const r = await fetch(`${API_URL}/landlord/pipeline`, { headers: { Authorization: `Bearer ${token}` } });
    const d = await r.json();
    setItems(d.pipeline || []);
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, filterTab]);

  const buyActive = items.filter(i => i.pipeline_type === 'buy' && isActiveDeal(i)).length;
  const sellActive = items.filter(i => i.pipeline_type === 'sell' && isActiveDeal(i)).length;
  const underOffer = items.filter(i => i.stage === 'under_offer').length;
  const completed = items.filter(i => i.stage === 'completed').length;

  const filtered = items.filter(i => {
    if (filterTab === 'Buying' && i.pipeline_type !== 'buy') return false;
    if (filterTab === 'Selling' && i.pipeline_type !== 'sell') return false;
    if (filterTab === 'Active' && !isActiveDeal(i)) return false;
    if (filterTab === 'Under Offer' && i.stage !== 'under_offer') return false;
    if (filterTab === 'Completed' && i.stage !== 'completed') return false;
    if (filterTab === 'Withdrawn' && i.stage !== 'withdrawn') return false;
    const q = search.toLowerCase().trim();
    if (q && !`${i.address} ${i.postcode || ''} ${i.agent_name || ''} ${i.notes || ''}`.toLowerCase().includes(q)) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const byStage = (stage: string) => filtered.filter(i => i.stage === stage);

  const moveStage = async (id: number, stage: string) => {
    await fetch(`${API_URL}/landlord/pipeline/${id}/stage`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ stage }),
    });
    load();
  };

  const del = async (id: number) => {
    if (!window.confirm('Remove this deal?')) return;
    await fetch(`${API_URL}/landlord/pipeline/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  const filterCount = (f: string) => {
    if (f === 'Buying') return items.filter(i => i.pipeline_type === 'buy').length;
    if (f === 'Selling') return items.filter(i => i.pipeline_type === 'sell').length;
    if (f === 'Active') return items.filter(isActiveDeal).length;
    if (f === 'Under Offer') return underOffer;
    if (f === 'Completed') return completed;
    if (f === 'Withdrawn') return items.filter(i => i.stage === 'withdrawn').length;
    return 0;
  };

  const CSS = `
    .sp-page { padding: 24px; max-width: 1400px; margin: 0 auto; font-family: 'DM Sans', system-ui, sans-serif; background: #eef2f6; min-height: 100vh; }
    .sp-banner-wrap { margin: -24px -24px 28px; padding: 10px 10px 14px; background: linear-gradient(145deg, #1e3a5f 0%, #2563eb 40%, #0369a1 72%, #0d9488 100%); border-radius: 0 0 22px 22px; box-shadow: 0 12px 40px rgba(37,99,235,0.25); }
    .sp-banner-glass { display: flex; align-items: center; justify-content: space-between; gap: 20px; flex-wrap: wrap; background: rgba(255,255,255,0.11); backdrop-filter: blur(22px); border: 1px solid rgba(255,255,255,0.28); border-radius: 18px; padding: 34px 38px; }
    .sp-banner-glass h1 { margin: 0; font-size: 34px; font-weight: 800; color: #fff; }
    .sp-banner-glass p { margin: 8px 0 0; font-size: 14px; color: rgba(255,255,255,0.88); }
    .sp-btn-add { background: #fff; color: #2563eb; border: none; border-radius: 10px; padding: 11px 24px; font-weight: 800; font-size: 14px; cursor: pointer; }
    .sp-kpi-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
    .sp-kpi { border-radius: 16px; padding: 26px 24px 22px; min-height: 118px; }
    .sp-kpi-white { background: ${UK.white}; border: 1.5px solid #e2e8f0; }
    .sp-kpi-blue { background: linear-gradient(145deg, #2563eb, #3b82f6); }
    .sp-kpi-teal { background: linear-gradient(145deg, #0d9488, #14b8a6); }
    .sp-kpi-gold { background: linear-gradient(145deg, #ca8a04, ${UK.gold}); }
    .sp-kpi .kl { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.09em; margin-bottom: 8px; }
    .sp-kpi-white .kl { color: #64748b; } .sp-kpi-blue .kl, .sp-kpi-teal .kl, .sp-kpi-gold .kl { color: rgba(255,255,255,0.85); }
    .sp-kpi .kv { font-size: 32px; font-weight: 800; font-family: 'DM Mono', monospace; }
    .sp-kpi-white .kv { color: #2563eb; } .sp-kpi-blue .kv, .sp-kpi-teal .kv, .sp-kpi-gold .kv { color: #fff; }
    .sp-toolbar { display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; align-items: center; }
    .sp-search { flex: 1; min-width: 220px; max-width: 420px; padding: 10px 16px; background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; }
    .sp-search input { border: none; outline: none; width: 100%; font-size: 14px; }
    .sp-filter { padding: 10px 36px 10px 14px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff; font-size: 13px; font-weight: 700; color: #2563eb; min-width: 200px; cursor: pointer; }
    .sp-toggle { display: flex; background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; overflow: hidden; }
    .sp-toggle button { padding: 10px 16px; border: none; background: transparent; font-weight: 700; font-size: 13px; cursor: pointer; color: #64748b; }
    .sp-toggle button.on { background: #2563eb; color: #fff; }
    .sp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 16px; }
    .sp-card { background: #fff; border-radius: 12px; border: 1px solid #e5e7eb; overflow: hidden; cursor: pointer; }
    .sp-accent { height: 4px; }
    .sp-card-body { padding: 16px 18px 12px; }
    .sp-card-foot { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; padding: 10px 14px 14px; border-top: 1px solid #f1f5f9; }
    .sp-act { padding: 8px 6px; border-radius: 8px; font-size: 11px; font-weight: 700; cursor: pointer; border: none; text-align: center; }
    .sp-board { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 14px; align-items: start; }
    .sp-col { background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 14px; padding: 14px; min-height: 120px; }
    @media (max-width: 900px) { .sp-kpi-row { grid-template-columns: repeat(2, 1fr); } }
  `;

  const DealCard = ({ item, compact = false }: { item: any; compact?: boolean }) => {
    const st = stageMeta(item.stage);
    return (
      <div className={compact ? 'sp-card' : 'sp-card'} onClick={() => navigate(`/landlord/pipeline/${item.id}`)} style={{ marginBottom: compact ? 10 : 0 }}>
        <div className="sp-accent" style={{ background: dealAccent(item) }} />
        <div className="sp-card-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
            <div style={{ fontWeight: 800, fontSize: 14 }}>{item.address}</div>
            <span style={{ background: st.bg, color: st.colour, padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700 }}>{st.label}</span>
          </div>
          <div style={{ fontSize: 12, color: '#64748b' }}>{item.postcode || '—'} · {item.pipeline_type === 'buy' ? 'Buy' : 'Sell'}</div>
          {(item.asking_price || item.offer_price) && (
            <div style={{ fontSize: 12, marginTop: 6 }}>
              {item.offer_price ? <strong style={{ color: UK.blue }}>{fmtMoney(item.offer_price)}</strong> : fmtMoney(item.asking_price)}
            </div>
          )}
          {item.accounting_recorded && <span style={{ fontSize: 10, background: '#dcfce7', color: '#16a34a', padding: '2px 6px', borderRadius: 4, fontWeight: 700, marginTop: 6, display: 'inline-block' }}>Accounted</span>}
        </div>
        {!compact && (
          <div className="sp-card-foot" onClick={e => e.stopPropagation()}>
            <button type="button" className="sp-act" style={{ background: '#2563eb', color: '#fff' }} onClick={() => navigate(`/landlord/pipeline/${item.id}`)}>View</button>
            <button type="button" className="sp-act" style={{ background: '#fff', color: UK.blue, border: `1.5px solid ${UK.blue}` }} onClick={() => navigate(`/landlord/pipeline/${item.id}/edit`)}>Edit</button>
            {isActiveDeal(item) && item.stage === 'exchanged' && (
              <button type="button" className="sp-act" style={{ background: '#16a34a', color: '#fff' }} onClick={() => navigate(`/landlord/pipeline/${item.id}`)}>Complete</button>
            )}
            <button type="button" className="sp-act" style={{ background: UK.red, color: '#fff' }} onClick={() => del(item.id)}>Delete</button>
          </div>
        )}
        {compact && (
          <div style={{ padding: '0 12px 10px', display: 'flex', gap: 4, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
            {PIPELINE_STAGES.filter(s => s.id !== item.stage && s.id !== 'withdrawn').slice(0, 2).map(s => (
              <button key={s.id} type="button" onClick={() => moveStage(item.id, s.id)} style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6, border: `1px solid ${s.colour}33`, background: s.bg, color: s.colour, cursor: 'pointer' }}>→ {s.label}</button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="sp-page">
      <style>{CSS}</style>

      <div className="sp-banner-wrap">
        <div className="sp-banner-glass">
          <div>
            <h1>Sales & Buy</h1>
            <p>{items.length} deal{items.length !== 1 ? 's' : ''} · {buyActive} buying · {sellActive} selling</p>
          </div>
          <button type="button" className="sp-btn-add" onClick={() => navigate('/landlord/pipeline/new')}>+ Add Deal</button>
        </div>
      </div>

      <div className="sp-kpi-row">
        <div className="sp-kpi sp-kpi-white"><div className="kl">Total Deals</div><div className="kv">{items.length}</div></div>
        <div className="sp-kpi sp-kpi-blue"><div className="kl">Buying</div><div className="kv">{buyActive}</div></div>
        <div className="sp-kpi sp-kpi-teal"><div className="kl">Selling</div><div className="kv">{sellActive}</div></div>
        <div className="sp-kpi sp-kpi-gold"><div className="kl">Under Offer</div><div className="kv">{underOffer}</div></div>
      </div>

      <div className="sp-toolbar">
        <div className="sp-search"><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search address, agent, notes…" /></div>
        <select className="sp-filter" value={filterTab} onChange={e => setFilterTab(e.target.value)}>
          {PIPELINE_FILTERS.map(f => <option key={f} value={f}>{f}{filterCount(f) > 0 && f !== 'All' ? ` (${filterCount(f)})` : ''}</option>)}
        </select>
        <div className="sp-toggle">
          <button type="button" className={viewMode === 'list' ? 'on' : ''} onClick={() => setViewMode('list')}>List</button>
          <button type="button" className={viewMode === 'board' ? 'on' : ''} onClick={() => setViewMode('board')}>Board</button>
        </div>
      </div>

      {loading ? <p style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading…</p> : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, background: '#fff', borderRadius: 12, border: '2px dashed #e5e7eb' }}>
          <div style={{ fontWeight: 700, fontSize: 18 }}>No deals yet</div>
          <button type="button" onClick={() => navigate('/landlord/pipeline/new')} style={{ marginTop: 12, padding: '10px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>+ Add Deal</button>
        </div>
      ) : viewMode === 'list' ? (
        <>
          <div className="sp-grid">{pageItems.map(item => <DealCard key={item.id} item={item} />)}</div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 20 }}>
              <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', fontWeight: 700, cursor: 'pointer' }}>Previous</button>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>Page {page} of {totalPages}</span>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', fontWeight: 700, cursor: 'pointer' }}>Next</button>
            </div>
          )}
        </>
      ) : (
        <div className="sp-board">
          {PIPELINE_STAGES.filter(s => s.id !== 'withdrawn' || byStage('withdrawn').length > 0).map(stage => {
            const cards = byStage(stage.id);
            const st = stageMeta(stage.id);
            return (
              <div key={stage.id} className="sp-col">
                <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: st.colour, marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
                  {st.label}<span style={{ background: st.bg, padding: '2px 8px', borderRadius: 12 }}>{cards.length}</span>
                </div>
                {cards.map(item => <DealCard key={item.id} item={item} compact />)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
